import React, { useEffect, useMemo, useState } from 'react';
import { User, Loader2 } from 'lucide-react';
import { useKiprData } from '@/lib/kiprData';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import Billing from './Billing';

const Profile: React.FC = () => {
  const { profile, kips, trainingBalance, updateProfile } = useKiprData();
  const { user, updatePassword } = useAuth();
  const [name, setName] = useState(profile.name);
  const [role, setRole] = useState(profile.role ?? 'ABA Therapist in Training');
  const [email, setEmail] = useState(user?.email ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [emailMessage, setEmailMessage] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    setName(profile.name);
    setRole(profile.role ?? 'ABA Therapist in Training');
  }, [profile.name, profile.role]);

  useEffect(() => {
    setEmail(user?.email ?? '');
  }, [user?.email]);

  const memberSince = useMemo(() => {
    const sourceDate = user?.created_at ?? profile.createdAt;
    if (!sourceDate) return 'Member since recently';
    return `Member since ${new Date(sourceDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}`;
  }, [profile.createdAt, user?.created_at]);

  const skills = [
    { name: 'Communication', value: profile.skills.communication, color: 'bg-blue-500' },
    { name: 'Accuracy', value: profile.skills.accuracy, color: 'bg-green-500' },
    { name: 'Empathy', value: profile.skills.empathy, color: 'bg-purple-500' },
  ];

  const handleProfileSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setProfileError(null);
    setProfileMessage(null);
    setProfileBusy(true);
    try {
      await updateProfile({ name: name.trim(), role: role.trim() });
      setProfileMessage('Profile details saved.');
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Unable to save profile details.');
    } finally {
      setProfileBusy(false);
    }
  };

  const handleEmailUpdate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase) return;
    setEmailError(null);
    setEmailMessage(null);
    setEmailBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: email.trim() });
      if (error) throw error;
      setEmailMessage('Email update requested. Please confirm the new address from your inbox.');
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Unable to update email.');
    } finally {
      setEmailBusy(false);
    }
  };

  const handlePasswordUpdate = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordMessage(null);
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordBusy(true);
    try {
      await updatePassword(newPassword);
      setPasswordMessage('Password updated successfully.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Unable to update password.');
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold font-poppins text-[#1A1A1A] mb-2">Profile</h1>
        <p className="text-[#555555] font-inter">Manage your account, billing, and skill development</p>
      </div>

      {/* User Info */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex items-center space-x-6">
          <div className="w-20 h-20 bg-[#121212] rounded-full flex items-center justify-center">
            <User size={32} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-poppins text-[#1A1A1A]">{profile.name}</h2>
            <p className="text-[#555555]">{profile.role ?? 'ABA Therapist in Training'}</p>
            <p className="text-sm text-[#777777] mt-1">{memberSince}</p>
          </div>
        </div>
      </div>

      <Billing trainingBalance={trainingBalance} />

      {/* Skill Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-6">Skill Development</h2>
        <div className="space-y-6">
          {skills.map((skill) => (
            <div key={skill.name}>
              <div className="flex justify-between mb-2">
                <span className="font-medium text-[#1A1A1A]">{skill.name}</span>
                <span className="text-[#555555]">{skill.value}%</span>
              </div>
              <div className="w-full bg-[#DADADA] rounded-full h-3">
                <div
                  className={`${skill.color} h-3 rounded-full transition-all duration-300`}
                  style={{ width: `${skill.value}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Account settings */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-6">Account settings</h2>
        <div className="space-y-8">
          <form onSubmit={handleProfileSave} className="space-y-4 border-b border-[#ECECEC] pb-6">
            <h3 className="font-semibold text-[#1A1A1A]">Profile details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="profile-name" className="block text-sm font-medium text-[#555555] mb-1.5">
                  Full name
                </label>
                <input
                  id="profile-name"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="w-full px-4 py-2.5 border border-[#DADADA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#121212]/20"
                  required
                />
              </div>
              <div>
                <label htmlFor="profile-role" className="block text-sm font-medium text-[#555555] mb-1.5">
                  Role
                </label>
                <input
                  id="profile-role"
                  type="text"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  className="w-full px-4 py-2.5 border border-[#DADADA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#121212]/20"
                  required
                />
              </div>
            </div>
            {profileError && <div className="text-sm text-red-700">{profileError}</div>}
            {profileMessage && <div className="text-sm text-emerald-700">{profileMessage}</div>}
            <button
              type="submit"
              disabled={profileBusy}
              className="inline-flex items-center gap-2 rounded-md bg-[#121212] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#2A2A2A] disabled:opacity-50"
            >
              {profileBusy && <Loader2 size={16} className="animate-spin" />}
              Save profile
            </button>
          </form>

          <form onSubmit={handleEmailUpdate} className="space-y-4 border-b border-[#ECECEC] pb-6">
            <h3 className="font-semibold text-[#1A1A1A]">Email address</h3>
            <div>
              <label htmlFor="account-email" className="block text-sm font-medium text-[#555555] mb-1.5">
                Account email
              </label>
              <input
                id="account-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full px-4 py-2.5 border border-[#DADADA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#121212]/20"
                required
              />
            </div>
            {emailError && <div className="text-sm text-red-700">{emailError}</div>}
            {emailMessage && <div className="text-sm text-emerald-700">{emailMessage}</div>}
            <button
              type="submit"
              disabled={emailBusy || !supabase}
              className="inline-flex items-center gap-2 rounded-md border border-[#DADADA] bg-white px-4 py-2.5 text-sm font-medium text-[#1A1A1A] hover:bg-[#FAFAFA] disabled:opacity-50"
            >
              {emailBusy && <Loader2 size={16} className="animate-spin" />}
              Update email
            </button>
          </form>

          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <h3 className="font-semibold text-[#1A1A1A]">Password</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-[#555555] mb-1.5">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  minLength={8}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="w-full px-4 py-2.5 border border-[#DADADA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#121212]/20"
                  required
                />
              </div>
              <div>
                <label htmlFor="confirm-new-password" className="block text-sm font-medium text-[#555555] mb-1.5">
                  Confirm password
                </label>
                <input
                  id="confirm-new-password"
                  type="password"
                  minLength={8}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="w-full px-4 py-2.5 border border-[#DADADA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#121212]/20"
                  required
                />
              </div>
            </div>
            {passwordError && <div className="text-sm text-red-700">{passwordError}</div>}
            {passwordMessage && <div className="text-sm text-emerald-700">{passwordMessage}</div>}
            <button
              type="submit"
              disabled={passwordBusy}
              className="inline-flex items-center gap-2 rounded-md border border-[#DADADA] bg-white px-4 py-2.5 text-sm font-medium text-[#1A1A1A] hover:bg-[#FAFAFA] disabled:opacity-50"
            >
              {passwordBusy && <Loader2 size={16} className="animate-spin" />}
              Change password
            </button>
          </form>
        </div>
      </div>

      {/* Achievement Stats */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-6">Achievements</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-[#121212] mb-2">{profile.stats.kipStreak}</div>
            <div className="text-[#555555]">Day Streak</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#121212] mb-2">{profile.stats.avgScore}</div>
            <div className="text-[#555555]">Avg Score</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#121212] mb-2">{kips.filter(kip => kip.completed).length}</div>
            <div className="text-[#555555]">Total Kips</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;